import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { usePermissions } from "../../PermissionsProvider";
import {
  WorkflowService,
  Workflow,
  WorkflowTestSet,
  WorkflowTestSetItem,
  WorkflowRun,
} from "../../services/WorkflowService";
import { SessionService } from "../../services/SessionService";
import { Session } from "../../model_gen/session";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Badge } from "../ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  Loader2Icon,
  PlusIcon,
  Trash2Icon,
  XIcon,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { notification } from "antd";
import moment from "moment";
import WorkflowRunDetailModal, { formatWorkflowDuration } from "../shared/WorkflowRunDetailModal";

const PAGE_SIZE = 10;

const WorkflowTestSetEdit: React.FC = () => {
  const { testSetId } = useParams<{ testSetId: string }>();
  const { apiKey } = usePermissions();
  const navigate = useNavigate();

  const [testSet, setTestSet] = useState<WorkflowTestSet | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  // Current items
  const [items, setItems] = useState<WorkflowTestSetItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPage, setItemsPage] = useState(1);
  const [isItemsLoading, setIsItemsLoading] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState<number[]>([]);

  // Outer tab: current vs add
  const [activeTab, setActiveTab] = useState<"current" | "add">("current");
  // Inner tab inside "add": workflow_runs vs sessions
  const [addSource, setAddSource] = useState<"workflow_runs" | "sessions">("workflow_runs");
  const [hideAdded, setHideAdded] = useState(true);

  // Available runs
  const [availableRuns, setAvailableRuns] = useState<WorkflowRun[]>([]);
  const [totalAvailable, setTotalAvailable] = useState(0);
  const [availablePage, setAvailablePage] = useState(1);
  const [isAvailableLoading, setIsAvailableLoading] = useState(false);
  const [selectedRunIds, setSelectedRunIds] = useState<number[]>([]);

  // Available sessions
  const [sessions, setSessions] = useState<Session[]>([]);
  const [totalSessions, setTotalSessions] = useState(0);
  const [sessionsPage, setSessionsPage] = useState(1);
  const [isSessionsLoading, setIsSessionsLoading] = useState(false);
  const [selectedSessionIds, setSelectedSessionIds] = useState<string[]>([]);
  const [importedSessionIds, setImportedSessionIds] = useState<Set<string>>(new Set());

  // Workflow filter
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [workflowFilters, setWorkflowFilters] = useState<number[]>([]);

  // Refresh trigger — increment to force re-fetch of available runs/sessions
  const [refreshKey, setRefreshKey] = useState(0);

  // Detail modal
  const [selectedRun, setSelectedRun] = useState<WorkflowRun | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    if (!testSetId) return;
    WorkflowService.getWorkflowTestSet(
      Number(testSetId),
      PAGE_SIZE,
      0,
      setIsLoading,
      apiKey
    ).subscribe(
      (data: WorkflowTestSet) => {
        setTestSet(data);
        setName(data.test_set_name || "");
        setDescription(data.test_set_description || "");
        setItems(data.items || []);
        setTotalItems(data.item_count || 0);
        setIsLoading(false);

        if (data.application_id) {
          WorkflowService.getWorkflows(() => {}, apiKey).subscribe(
            (wfs: Workflow[]) => {
              setWorkflows(
                (wfs || []).filter((w) => w.application_id === data.application_id)
              );
            },
            () => {}
          );
        }
      },
      (error: any) => {
        console.error(error);
        setIsLoading(false);
      }
    );
  }, [testSetId, apiKey]);

  // Fetch imported session IDs so we can filter them out of the sessions list
  const refreshImportedSessionIds = () => {
    if (!testSetId) return;
    WorkflowService.getImportedSessionIds(Number(testSetId), () => {}, apiKey).subscribe(
      (data: { session_ids?: string[] }) => {
        setImportedSessionIds(new Set(data.session_ids || []));
      },
      () => {}
    );
  };

  useEffect(() => {
    if (!testSetId) return;
    refreshImportedSessionIds();
  }, [testSetId, apiKey]);

  // Fetch current items when page changes (beyond initial load)
  useEffect(() => {
    if (!testSetId || itemsPage === 1) return;
    setIsItemsLoading(true);
    const offset = (itemsPage - 1) * PAGE_SIZE;
    WorkflowService.getWorkflowTestSet(Number(testSetId), PAGE_SIZE, offset, () => {}, apiKey).subscribe(
      (data: WorkflowTestSet) => {
        setItems(data.items || []);
        setTotalItems(data.item_count || 0);
        setIsItemsLoading(false);
      },
      () => setIsItemsLoading(false)
    );
  }, [testSetId, itemsPage, apiKey]);

  // Fetch available runs
  useEffect(() => {
    if (activeTab !== "add" || addSource !== "workflow_runs" || !testSet?.application_id) return;
    setIsAvailableLoading(true);
    const offset = (availablePage - 1) * PAGE_SIZE;
    const filterIds = workflowFilters.length > 0 ? workflowFilters : undefined;
    WorkflowService.getWorkflowRuns(testSet.application_id, offset, PAGE_SIZE, () => {}, apiKey, filterIds).subscribe(
      (data: { runs?: WorkflowRun[]; total?: number }) => {
        setAvailableRuns(data.runs || []);
        setTotalAvailable(data.total || 0);
        setIsAvailableLoading(false);
      },
      () => setIsAvailableLoading(false)
    );
  }, [activeTab, addSource, availablePage, workflowFilters.join(","), testSet?.application_id, apiKey, refreshKey]);

  // Fetch available sessions
  useEffect(() => {
    if (activeTab !== "add" || addSource !== "sessions" || !testSet?.application_id) return;
    setIsSessionsLoading(true);
    const offset = (sessionsPage - 1) * PAGE_SIZE;
    SessionService.getApplicationSessions(testSet.application_id, offset, PAGE_SIZE, () => {}, apiKey).subscribe(
      (data: { sessions?: Session[]; total?: number }) => {
        setSessions(data.sessions || []);
        setTotalSessions(data.total ?? 0);
        setIsSessionsLoading(false);
      },
      () => setIsSessionsLoading(false)
    );
  }, [activeTab, addSource, sessionsPage, testSet?.application_id, apiKey, refreshKey]);

  const handleSave = () => {
    if (!testSetId) return;
    setIsSaving(true);
    WorkflowService.updateWorkflowTestSet(Number(testSetId), name, description, () => {}, apiKey).subscribe(
      () => {
        notification.success({ message: "Test set updated" });
        navigate(`/test/workflow-test-sets/${testSetId}`);
      },
      (error: any) => {
        console.error(error);
        notification.error({ message: "Error updating test set", description: String(error) });
        setIsSaving(false);
      }
    );
  };

  const handleRemoveSelected = () => {
    if (selectedItemIds.length === 0 || !testSetId) return;
    setIsItemsLoading(true);
    let completed = 0;
    const total = selectedItemIds.length;
    selectedItemIds.forEach((itemId) => {
      WorkflowService.deleteWorkflowTestSetItem(Number(testSetId), itemId, () => {}, apiKey).subscribe(
        () => { completed++; if (completed === total) { setSelectedItemIds([]); refreshItems(); } },
        () => { completed++; if (completed === total) { setSelectedItemIds([]); refreshItems(); } }
      );
    });
  };

  const handleAddRuns = () => {
    if (selectedRunIds.length === 0 || !testSetId) return;
    setIsAvailableLoading(true);
    WorkflowService.addWorkflowTestSetItems(Number(testSetId), selectedRunIds, null, null, () => {}, apiKey).subscribe(
      (resp: any) => {
        const added = resp?.total_items ?? selectedRunIds.length;
        const errors = resp?.errors ?? [];
        if (errors.length > 0) {
          console.warn("Add runs errors:", errors);
          notification.warning({ message: `Added ${added} run(s), ${errors.length} failed` });
        } else {
          notification.success({ message: `Added ${added} run(s)` });
        }
        setSelectedRunIds([]);
        setIsAvailableLoading(false);
        refreshItems();
      },
      (error: any) => {
        console.error("Add runs error:", error);
        notification.error({ message: "Error adding runs", description: typeof error === "string" ? error : undefined });
        setIsAvailableLoading(false);
      }
    );
  };

  const handleAddSessions = () => {
    if (selectedSessionIds.length === 0 || !testSetId) return;
    const transformWf = workflows[0];
    if (!transformWf) {
      notification.error({ message: "No workflow available for session transform" });
      return;
    }
    setIsSessionsLoading(true);
    WorkflowService.addWorkflowTestSetItems(
      Number(testSetId), null, selectedSessionIds, transformWf.id, () => {}, apiKey
    ).subscribe(
      (resp: any) => {
        const added = resp?.total_items ?? selectedSessionIds.length;
        const errors = resp?.errors ?? [];
        if (errors.length > 0) {
          console.warn("Import session errors:", errors);
          notification.warning({ message: `Imported ${added} session(s), ${errors.length} failed` });
        } else {
          notification.success({ message: `Imported ${added} session(s)` });
        }
        setSelectedSessionIds([]);
        setIsSessionsLoading(false);
        refreshItems();
      },
      (error: any) => {
        console.error("Import session error:", error);
        notification.error({ message: "Error importing sessions", description: typeof error === "string" ? error : undefined });
        setIsSessionsLoading(false);
      }
    );
  };

  const refreshItems = () => {
    if (!testSetId) return;
    setIsItemsLoading(true);
    const offset = (itemsPage - 1) * PAGE_SIZE;
    WorkflowService.getWorkflowTestSet(Number(testSetId), PAGE_SIZE, offset, () => {}, apiKey).subscribe(
      (data: WorkflowTestSet) => {
        setItems(data.items || []);
        setTotalItems(data.item_count || 0);
        setIsItemsLoading(false);
      },
      () => setIsItemsLoading(false)
    );
    refreshImportedSessionIds();
    setRefreshKey((k) => k + 1);
  };

  const formatDate = (s: string | undefined) => {
    if (!s) return "-";
    return moment(s).format("MMM D, YYYY h:mm A");
  };

  const formatDateMs = (ts: number | undefined) => {
    if (!ts || ts <= 0) return "-";
    return moment(ts).format("MMM D, YYYY h:mm A");
  };

  const toggleWorkflowFilter = (wfId: number) => {
    setWorkflowFilters((prev) =>
      prev.includes(wfId) ? prev.filter((id) => id !== wfId) : [...prev, wfId]
    );
    setAvailablePage(1);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="h-10 bg-muted rounded w-64 animate-pulse" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const itemsTotalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const availableTotalPages = Math.max(1, Math.ceil(totalAvailable / PAGE_SIZE));
  const sessionsTotalPages = Math.max(1, Math.ceil(totalSessions / PAGE_SIZE));

  const existingRunIds = new Set(items.map((item) => item.workflow_run_id).filter(Boolean));

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        {/* Header */}
        <Card className="border-border">
          <CardContent className="p-8">
            <nav className="mb-6">
              <button
                onClick={() => navigate(`/test/workflow-test-sets/${testSetId}`)}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
              >
                <ChevronLeftIcon className="w-4 h-4" />
                Back to Test Set
              </button>
            </nav>
            <div className="space-y-1">
              <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">Edit Test Set</h1>
              <p className="text-lg text-muted-foreground font-gc-reg">Modify details and manage workflow runs</p>
            </div>
          </CardContent>
        </Card>

        {/* Form */}
        <Card className="border-border">
          <CardContent className="p-8 space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-gc-med text-foreground">Name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description" className="text-sm font-gc-med text-foreground">Description</Label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>
          </CardContent>
        </Card>

        {/* Items Tabs */}
        <Card className="border-border">
          <CardContent className="p-0">
            <div className="border-b border-border">
              <nav className="flex space-x-8 px-8 py-4">
                <button
                  onClick={() => setActiveTab("current")}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 text-sm font-gc-med rounded-md transition-colors",
                    activeTab === "current"
                      ? "bg-maitai-green/10 text-maitai-green"
                      : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                  )}
                >
                  Current Items ({totalItems})
                </button>
                <button
                  onClick={() => setActiveTab("add")}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 text-sm font-gc-med rounded-md transition-colors",
                    activeTab === "add"
                      ? "bg-maitai-green/10 text-maitai-green"
                      : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                  )}
                >
                  <PlusIcon className="w-4 h-4" />
                  Add Items
                </button>
              </nav>
            </div>

            <div className="p-8">
              {activeTab === "current" ? (
                <CurrentItemsTab
                  items={items}
                  loading={isItemsLoading}
                  selectedIds={selectedItemIds}
                  onSelectionChange={setSelectedItemIds}
                  onRemoveSelected={handleRemoveSelected}
                  onRunClick={(item) => {
                    if (item.workflow_run) { setSelectedRun(item.workflow_run); setDetailOpen(true); }
                  }}
                  currentPage={itemsPage}
                  totalPages={itemsTotalPages}
                  onPageChange={setItemsPage}
                  formatDate={formatDate}
                />
              ) : (
                <div className="space-y-6">
                  {/* Inner source toggle */}
                  <div className="border-b border-border">
                    <nav className="flex gap-4">
                      <button
                        type="button"
                        onClick={() => setAddSource("workflow_runs")}
                        className={cn(
                          "pb-3 text-sm font-gc-med border-b-2 -mb-px transition-colors",
                          addSource === "workflow_runs"
                            ? "border-maitai-green text-maitai-green"
                            : "border-transparent text-muted-foreground hover:text-foreground"
                        )}
                      >
                        Workflow Runs
                      </button>
                      <button
                        type="button"
                        onClick={() => setAddSource("sessions")}
                        className={cn(
                          "pb-3 text-sm font-gc-med border-b-2 -mb-px transition-colors",
                          addSource === "sessions"
                            ? "border-maitai-green text-maitai-green"
                            : "border-transparent text-muted-foreground hover:text-foreground"
                        )}
                      >
                        Sessions
                      </button>
                    </nav>
                  </div>

                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="hide-added"
                      checked={hideAdded}
                      onCheckedChange={(c) => setHideAdded(c === true)}
                    />
                    <Label htmlFor="hide-added" className="text-sm text-muted-foreground font-gc-reg cursor-pointer select-none">
                      Hide already added items
                    </Label>
                  </div>

                  {addSource === "workflow_runs" ? (
                    <AddRunsSection
                      runs={availableRuns}
                      loading={isAvailableLoading}
                      existingRunIds={existingRunIds}
                      hideAdded={hideAdded}
                      selectedIds={selectedRunIds}
                      onSelectionChange={setSelectedRunIds}
                      onAddSelected={handleAddRuns}
                      workflows={workflows}
                      workflowFilters={workflowFilters}
                      onToggleFilter={toggleWorkflowFilter}
                      onClearFilters={() => { setWorkflowFilters([]); setAvailablePage(1); }}
                      currentPage={availablePage}
                      totalPages={availableTotalPages}
                      onPageChange={setAvailablePage}
                      formatDate={formatDate}
                      onRunClick={(run) => { setSelectedRun(run); setDetailOpen(true); }}
                    />
                  ) : (
                    <AddSessionsSection
                      sessions={sessions}
                      loading={isSessionsLoading}
                      importedSessionIds={importedSessionIds}
                      hideAdded={hideAdded}
                      selectedIds={selectedSessionIds}
                      onSelectionChange={setSelectedSessionIds}
                      onAddSelected={handleAddSessions}
                      currentPage={sessionsPage}
                      totalPages={sessionsTotalPages}
                      onPageChange={setSessionsPage}
                      formatDate={formatDateMs}
                    />
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <Button variant="outline" onClick={() => navigate(`/test/workflow-test-sets/${testSetId}`)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !name.trim()} className="gap-2">
            {isSaving && <Loader2Icon className="w-4 h-4 animate-spin" />}
            Save Changes
          </Button>
        </div>
      </div>

      <WorkflowRunDetailModal
        run={selectedRun}
        open={detailOpen}
        onOpenChange={(open) => { setDetailOpen(open); if (!open) setSelectedRun(null); }}
      />
    </div>
  );
};

// ---------- Current Items ----------

interface CurrentItemsTabProps {
  items: WorkflowTestSetItem[];
  loading: boolean;
  selectedIds: number[];
  onSelectionChange: (ids: number[]) => void;
  onRemoveSelected: () => void;
  onRunClick: (item: WorkflowTestSetItem) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (p: number) => void;
  formatDate: (s: string | undefined) => string;
}

const CurrentItemsTab: React.FC<CurrentItemsTabProps> = ({
  items, loading, selectedIds, onSelectionChange, onRemoveSelected,
  onRunClick, currentPage, totalPages, onPageChange, formatDate,
}) => {
  const allSelected = items.length > 0 && items.every((i) => selectedIds.includes(i.id));
  const someSelected = items.some((i) => selectedIds.includes(i.id));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-gc-med text-foreground">Current Items</h3>
          <p className="text-sm text-muted-foreground">Workflow runs in this test set</p>
        </div>
        {selectedIds.length > 0 && (
          <Button variant="destructive" size="sm" onClick={onRemoveSelected} className="gap-2">
            <Trash2Icon className="w-4 h-4" />
            Remove Selected ({selectedIds.length})
          </Button>
        )}
      </div>

      <div className="border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={someSelected && !allSelected ? "indeterminate" : allSelected}
                  onCheckedChange={(c) => {
                    const ids = items.map((i) => i.id);
                    onSelectionChange(c === true ? Array.from(new Set([...selectedIds, ...ids])) : selectedIds.filter((id) => !ids.includes(id)));
                  }}
                  disabled={loading || items.length === 0}
                />
              </TableHead>
              <TableHead>Session ID</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No items in this test set.</TableCell></TableRow>
            ) : (
              items.map((item) => {
                const run = item.workflow_run;
                const runAny = run as any;
                const isImported = runAny?.source === "IMPORTED" || !!runAny?.source_session_id;
                const isSelected = selectedIds.includes(item.id);
                return (
                  <TableRow key={item.id} className="cursor-pointer hover:bg-accent/20 transition-colors" onClick={() => onRunClick(item)}>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={isSelected} onCheckedChange={(c) => onSelectionChange(c ? [...selectedIds, item.id] : selectedIds.filter((x) => x !== item.id))} />
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {run?.session_id ? (run.session_id.length > 20 ? `...${run.session_id.slice(-16)}` : run.session_id) : "-"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={cn("text-xs", isImported ? "border-blue-500/30 text-blue-500" : "border-muted-foreground/30 text-muted-foreground")}>
                        {isImported ? "Imported" : "Executed"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm font-mono">{run ? formatWorkflowDuration(run.profile) : "-"}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{formatDate(run?.date_created)}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
      {totalPages > 1 && <PaginationBar current={currentPage} total={totalPages} onChange={onPageChange} loading={loading} />}
    </div>
  );
};

// ---------- Add Runs Section ----------

interface AddRunsSectionProps {
  runs: WorkflowRun[];
  loading: boolean;
  existingRunIds: Set<number>;
  hideAdded: boolean;
  selectedIds: number[];
  onSelectionChange: (ids: number[]) => void;
  onAddSelected: () => void;
  workflows: Workflow[];
  workflowFilters: number[];
  onToggleFilter: (id: number) => void;
  onClearFilters: () => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (p: number) => void;
  formatDate: (s: string | undefined) => string;
  onRunClick: (run: WorkflowRun) => void;
}

const AddRunsSection: React.FC<AddRunsSectionProps> = ({
  runs, loading, existingRunIds, hideAdded, selectedIds, onSelectionChange,
  onAddSelected, workflows, workflowFilters, onToggleFilter, onClearFilters,
  currentPage, totalPages, onPageChange, formatDate, onRunClick,
}) => {
  const eligibleRuns = runs.filter((r) => !existingRunIds.has(r.id));
  const displayRuns = hideAdded ? eligibleRuns : runs;
  const allSelected = eligibleRuns.length > 0 && eligibleRuns.every((r) => selectedIds.includes(r.id));
  const someSelected = eligibleRuns.some((r) => selectedIds.includes(r.id));
  const hasFilters = workflowFilters.length > 0;

  const getWorkflowName = (run: WorkflowRun): string => {
    const runAny = run as any;
    if (runAny.workflow_name) return runAny.workflow_name;
    const wf = workflows.find((w) => w.id === run.workflow_id);
    return wf?.workflow_name ?? "-";
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-gc-med text-foreground">Add Workflow Runs</h3>
          <p className="text-sm text-muted-foreground">Select runs to add to this test set</p>
        </div>
        {selectedIds.length > 0 && (
          <Button size="sm" onClick={onAddSelected} className="gap-2">
            <PlusIcon className="w-4 h-4" />
            Add Selected ({selectedIds.length})
          </Button>
        )}
      </div>

      {workflows.length > 1 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-gc-med text-foreground">Filter by Workflow</Label>
            {hasFilters && (
              <button type="button" onClick={onClearFilters} className="text-xs text-muted-foreground hover:text-foreground transition-colors">Clear filters</button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {workflows.map((wf) => {
              const isActive = workflowFilters.includes(wf.id);
              return (
                <Badge
                  key={wf.id}
                  variant={isActive ? "default" : "outline"}
                  className={cn("cursor-pointer select-none transition-colors", isActive ? "bg-maitai-green hover:bg-maitai-green/80 text-white border-maitai-green" : "hover:bg-accent/50")}
                  onClick={() => onToggleFilter(wf.id)}
                >
                  {wf.workflow_name}
                  {isActive && <XIcon className="w-3 h-3 ml-1" />}
                </Badge>
              );
            })}
          </div>
        </div>
      )}

      <div className="border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={someSelected && !allSelected ? "indeterminate" : allSelected}
                  onCheckedChange={(c) => {
                    const ids = eligibleRuns.map((r) => r.id);
                    onSelectionChange(c === true ? Array.from(new Set([...selectedIds, ...ids])) : selectedIds.filter((id) => !ids.includes(id)));
                  }}
                  disabled={loading || eligibleRuns.length === 0}
                />
              </TableHead>
              <TableHead>Session ID</TableHead>
              <TableHead>Workflow</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
            ) : displayRuns.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">{hasFilters ? "No runs for the selected filters." : "No available workflow runs."}</TableCell></TableRow>
            ) : (
              displayRuns.map((run) => {
                const alreadyAdded = existingRunIds.has(run.id);
                const isSelected = selectedIds.includes(run.id);
                return (
                  <TableRow key={run.id} className={cn("transition-colors", alreadyAdded ? "opacity-40" : "cursor-pointer hover:bg-accent/20")} onClick={() => !alreadyAdded && onRunClick(run)}>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={isSelected} onCheckedChange={(c) => onSelectionChange(c ? [...selectedIds, run.id] : selectedIds.filter((x) => x !== run.id))} disabled={alreadyAdded} />
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {run.session_id?.length > 20 ? `...${run.session_id.slice(-16)}` : run.session_id ?? "-"}
                      {alreadyAdded && <span className="ml-2 text-xs text-muted-foreground italic">already added</span>}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{getWorkflowName(run)}</TableCell>
                    <TableCell className="text-muted-foreground text-sm font-mono">{formatWorkflowDuration(run.profile)}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{formatDate(run.date_created)}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
      {totalPages > 1 && <PaginationBar current={currentPage} total={totalPages} onChange={onPageChange} loading={loading} />}
    </div>
  );
};

// ---------- Add Sessions Section ----------

interface AddSessionsSectionProps {
  sessions: Session[];
  loading: boolean;
  importedSessionIds: Set<string>;
  hideAdded: boolean;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  onAddSelected: () => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (p: number) => void;
  formatDate: (ts: number | undefined) => string;
}

const AddSessionsSection: React.FC<AddSessionsSectionProps> = ({
  sessions, loading, importedSessionIds, hideAdded, selectedIds, onSelectionChange,
  onAddSelected, currentPage, totalPages, onPageChange, formatDate,
}) => {
  const eligibleSessions = sessions.filter((s) => !importedSessionIds.has(s.session_id));
  const displaySessions = hideAdded ? eligibleSessions : sessions;
  const allSelected = eligibleSessions.length > 0 && eligibleSessions.every((s) => selectedIds.includes(s.session_id));
  const someSelected = eligibleSessions.some((s) => selectedIds.includes(s.session_id));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-gc-med text-foreground">Import Sessions</h3>
          <p className="text-sm text-muted-foreground">Sessions will be converted to workflow runs via transform_session</p>
        </div>
        {selectedIds.length > 0 && (
          <Button size="sm" onClick={onAddSelected} className="gap-2">
            <PlusIcon className="w-4 h-4" />
            Import Selected ({selectedIds.length})
          </Button>
        )}
      </div>

      <div className="border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={someSelected && !allSelected ? "indeterminate" : allSelected}
                  onCheckedChange={(c) => {
                    const ids = eligibleSessions.map((s) => s.session_id);
                    onSelectionChange(c === true ? Array.from(new Set([...selectedIds, ...ids])) : selectedIds.filter((id) => !ids.includes(id)));
                  }}
                  disabled={loading || eligibleSessions.length === 0}
                />
              </TableHead>
              <TableHead>Session ID</TableHead>
              <TableHead>Requests</TableHead>
              <TableHead>Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">Loading sessions...</TableCell></TableRow>
            ) : displaySessions.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No sessions found for this application.</TableCell></TableRow>
            ) : (
              displaySessions.map((session) => {
                const alreadyImported = importedSessionIds.has(session.session_id);
                const isSelected = selectedIds.includes(session.session_id);
                return (
                  <TableRow key={session.session_id} className={cn("transition-colors", alreadyImported ? "opacity-40" : "")}>
                    <TableCell>
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(c) => onSelectionChange(c ? [...selectedIds, session.session_id] : selectedIds.filter((id) => id !== session.session_id))}
                        disabled={alreadyImported}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {session.session_id.length > 20 ? `...${session.session_id.slice(-16)}` : session.session_id}
                      {alreadyImported && <span className="ml-2 text-xs text-muted-foreground italic">already imported</span>}
                    </TableCell>
                    <TableCell>{session.request_count ?? "-"}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{formatDate(session.date_ended ?? session.date_created)}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
      {totalPages > 1 && <PaginationBar current={currentPage} total={totalPages} onChange={onPageChange} loading={loading} />}
    </div>
  );
};

// ---------- Shared pagination ----------

const PaginationBar: React.FC<{ current: number; total: number; onChange: (p: number) => void; loading: boolean }> = ({
  current, total, onChange, loading,
}) => (
  <div className="flex items-center justify-between pt-2">
    <div className="text-sm text-muted-foreground">Page {current} of {total}</div>
    <div className="flex gap-2">
      <Button variant="outline" size="sm" onClick={() => onChange(Math.max(1, current - 1))} disabled={current <= 1 || loading}>
        <ChevronLeftIcon className="w-4 h-4" />
      </Button>
      <Button variant="outline" size="sm" onClick={() => onChange(Math.min(total, current + 1))} disabled={current >= total || loading}>
        <ChevronRightIcon className="w-4 h-4" />
      </Button>
    </div>
  </div>
);

export default WorkflowTestSetEdit;
